/**
 * Inventory Batches Management Hook
 *
 * User Story: US-P6-001 - Inventory Management
 * Step 3: Batch Receipt (Shipment Entry)
 *
 * Provides hooks for:
 * - Fetching inventory batches with filters
 * - Recording new shipments (creates batch with auto-generated code)
 * - Updating batch metadata (notes, invoice reference)
 * - Deleting unused batches
 * - Getting available batches for an item (FIFO order)
 * - Getting batch usage history
 */

import { useState, useEffect, useCallback, useMemo } from 'react'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY
const FUNCTION_URL = `${SUPABASE_URL}/functions/v1/inventory-batches`

/**
 * Call the inventory-batches edge function
 */
async function callInventoryBatchesFunction(action, data = {}) {
  const response = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'apikey': SUPABASE_ANON_KEY
    },
    body: JSON.stringify({ action, ...data }),
  })

  const result = await response.json()

  if (!response.ok) {
    throw new Error(result.error || result.message || 'Request failed')
  }

  return result
}

/**
 * Main hook for inventory batches management
 *
 * @param {Object} options - Hook options
 * @param {string} options.itemId - Filter by item ID
 * @param {string} options.status - Filter by status (available, in_use, depleted)
 * @returns {Object} - batches, isLoading, error, recordShipment, updateBatch, deleteBatch, refetch, filterByItem, filterByStatus
 */
export function useInventoryBatches(options = {}) {
  const { itemId: initialItemId = null, status: initialStatus = null } = options

  const [batches, setBatches] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [itemFilter, setItemFilter] = useState(initialItemId)
  const [statusFilter, setStatusFilter] = useState(initialStatus)

  /**
   * Fetch batches from the API
   */
  const fetchBatches = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const params = {}
      if (itemFilter) params.item_id = itemFilter
      if (statusFilter) params.status = statusFilter

      const result = await callInventoryBatchesFunction('list', params)
      setBatches(result.data || [])
    } catch (err) {
      console.error('Error fetching inventory batches:', err)
      setError(err.message)
      setBatches([])
    } finally {
      setIsLoading(false)
    }
  }, [itemFilter, statusFilter])

  /**
   * Get batches grouped by status
   */
  const batchesByStatus = useMemo(() => {
    const groups = {
      available: [],
      in_use: [],
      depleted: []
    }

    for (const batch of batches) {
      if (groups[batch.status]) {
        groups[batch.status].push(batch)
      }
    }

    return groups
  }, [batches])

  /**
   * Get batch statistics
   */
  const batchStats = useMemo(() => {
    return {
      total: batches.length,
      available: batchesByStatus.available.length,
      in_use: batchesByStatus.in_use.length,
      depleted: batchesByStatus.depleted.length,
      totalValue: batches.reduce((sum, b) => sum + (b.total_cost || 0), 0),
      totalRemaining: batches.reduce((sum, b) => sum + (b.quantity_remaining || 0), 0)
    }
  }, [batches, batchesByStatus])

  /**
   * Record a new shipment (creates a batch)
   *
   * @param {Object} shipmentData - Shipment data
   * @param {string} shipmentData.item_id - Inventory item ID (required)
   * @param {string} shipmentData.supplier_id - Supplier ID (optional, defaults to item's preferred)
   * @param {string} shipmentData.purchase_date - Date of purchase (optional, defaults to today)
   * @param {number} shipmentData.quantity - Quantity in PURCHASE units (required)
   * @param {number} shipmentData.quantity_override - Actual quantity in BASE units (optional, overrides calculated)
   * @param {number} shipmentData.product_cost - Cost of goods only (required)
   * @param {number} shipmentData.delivery_cost - Delivery/shipping cost (optional, defaults to 0)
   * @param {string} shipmentData.invoice_reference - Invoice/receipt reference (optional)
   * @param {string} shipmentData.notes - Additional notes (optional)
   * @returns {Promise<Object>} - Created batch with meta information including cost breakdown
   */
  const recordShipment = useCallback(async (shipmentData) => {
    try {
      setError(null)
      const result = await callInventoryBatchesFunction('create', shipmentData)

      // Add to local state
      if (result.data) {
        setBatches(prev => [result.data, ...prev])
      }

      return result
    } catch (err) {
      console.error('Error recording shipment:', err)
      setError(err.message)
      throw err
    }
  }, [])

  /**
   * Update an existing batch
   * Allowed fields: invoice_reference, notes, product_cost, delivery_cost
   *
   * @param {string} id - Batch ID
   * @param {Object} updates - Fields to update
   * @param {string} [updates.invoice_reference] - Invoice/receipt reference
   * @param {string} [updates.notes] - Additional notes
   * @param {number} [updates.product_cost] - Cost of goods (for corrections)
   * @param {number} [updates.delivery_cost] - Delivery cost (for corrections)
   * @returns {Promise<Object>} - Updated batch
   */
  const updateBatch = useCallback(async (id, updates) => {
    try {
      setError(null)
      const result = await callInventoryBatchesFunction('update', { id, ...updates })

      // Update local state
      if (result.data) {
        setBatches(prev =>
          prev.map(batch => batch.id === id ? result.data : batch)
        )
      }

      return result.data
    } catch (err) {
      console.error('Error updating batch:', err)
      setError(err.message)
      throw err
    }
  }, [])

  /**
   * Delete an unused batch
   *
   * @param {string} id - Batch ID
   * @returns {Promise<void>}
   */
  const deleteBatch = useCallback(async (id) => {
    try {
      setError(null)
      await callInventoryBatchesFunction('delete', { id })

      // Remove from local state
      setBatches(prev => prev.filter(batch => batch.id !== id))
    } catch (err) {
      console.error('Error deleting batch:', err)
      setError(err.message)
      throw err
    }
  }, [])

  /**
   * Get available batches for a specific item (FIFO order)
   *
   * @param {string} itemId - Inventory item ID
   * @returns {Promise<Object>} - Available batches and total quantity
   */
  const getAvailableBatches = useCallback(async (itemId) => {
    try {
      const result = await callInventoryBatchesFunction('available', { item_id: itemId })
      return result.data
    } catch (err) {
      console.error('Error getting available batches:', err)
      throw err
    }
  }, [])

  /**
   * Get batch usage history
   *
   * @param {string} batchId - Batch ID
   * @returns {Promise<Object>} - Batch with usage history
   */
  const getBatchUsageHistory = useCallback(async (batchId) => {
    try {
      const result = await callInventoryBatchesFunction('get', { id: batchId })
      return result.data
    } catch (err) {
      console.error('Error getting batch usage history:', err)
      throw err
    }
  }, [])

  /**
   * Filter batches by item
   *
   * @param {string|null} itemId - Item ID to filter by, or null to clear filter
   */
  const filterByItem = useCallback((itemId) => {
    setItemFilter(itemId)
  }, [])

  /**
   * Filter batches by status
   *
   * @param {string|null} status - Status to filter by, or null to clear filter
   */
  const filterByStatus = useCallback((status) => {
    setStatusFilter(status)
  }, [])

  // Initial fetch
  useEffect(() => {
    fetchBatches()
  }, [fetchBatches])

  return {
    batches,
    batchesByStatus,
    batchStats,
    isLoading,
    error,
    itemFilter,
    statusFilter,
    recordShipment,
    updateBatch,
    deleteBatch,
    getAvailableBatches,
    getBatchUsageHistory,
    refetch: fetchBatches,
    filterByItem,
    filterByStatus,
  }
}

/**
 * Hook for fetching a single inventory batch
 *
 * @param {string} batchId - Batch ID
 * @returns {Object} - batch, isLoading, error, refetch
 */
export function useInventoryBatch(batchId) {
  const [batch, setBatch] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchBatch = useCallback(async () => {
    if (!batchId) {
      setBatch(null)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      const result = await callInventoryBatchesFunction('get', { id: batchId })
      setBatch(result.data)
    } catch (err) {
      console.error('Error fetching inventory batch:', err)
      setError(err.message)
      setBatch(null)
    } finally {
      setIsLoading(false)
    }
  }, [batchId])

  useEffect(() => {
    fetchBatch()
  }, [fetchBatch])

  return { batch, isLoading, error, refetch: fetchBatch }
}

/**
 * Hook for getting available batches for an item
 *
 * @param {string} itemId - Inventory item ID
 * @returns {Object} - batches, totalAvailable, isLoading, error, refetch
 */
export function useAvailableBatches(itemId) {
  const [batches, setBatches] = useState([])
  const [totalAvailable, setTotalAvailable] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchAvailableBatches = useCallback(async () => {
    if (!itemId) {
      setBatches([])
      setTotalAvailable(0)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      const result = await callInventoryBatchesFunction('available', { item_id: itemId })
      setBatches(result.data?.batches || [])
      setTotalAvailable(result.data?.total_available || 0)
    } catch (err) {
      console.error('Error fetching available batches:', err)
      setError(err.message)
      setBatches([])
      setTotalAvailable(0)
    } finally {
      setIsLoading(false)
    }
  }, [itemId])

  useEffect(() => {
    fetchAvailableBatches()
  }, [fetchAvailableBatches])

  return { batches, totalAvailable, isLoading, error, refetch: fetchAvailableBatches }
}

export default useInventoryBatches
